import React from "react";
import { Box, IconButton, TextField, Typography } from "@mui/material";
import { useState, useEffect } from "react";
import CheckIcon from "@mui/icons-material/Check";
import { Autocomplete } from "@mui/material";
import AppTitleTypography from "./common/ui/AppTitleTypography";
import { isAgenda } from "../utils/agendaUtils";
import { AGENDA } from "../constants";
import { AGENDA_DATA } from "../constants/agenda";
import StandaloneSpeaker from "./StandaloneSpeaker";
import { Speaker } from "../interfaces/home/IMainContainer";
import EditIcon from "@mui/icons-material/Edit";
import { IAgendaProps } from "../interfaces/home/IAgenda";
import { IUserDetailsPermissionState } from "../interfaces/store/IUserDetailsPermissionState";
import { hasPermission } from "../utils/permissionUtil";
import { PERMISSION } from "../constants/userPermission";
import { useSelector } from "react-redux";

const Agenda: React.FC<IAgendaProps> = ({
  agenda,
  index,
  selectedValue,
  getStatusColor,
  handleTitleSaveClick,
  agendaOptions,
  setSelectedValue,
  navigateSpeechId,
  handleTitleEditClick,
  agendaStart,
  generateSummary,
  currentAgenda,
  handlePauseStopAgenda,
  handleAddAgendaStart,
  handleAddAgendaEnd,
  renderedSpeakers,
  agendaRefs,
  isAgendaBroadcasting,
  transcriptions,
  agendaStartIndex,
  successiveAgendaStartIndex,
  handleEditSpeaker,
  handleDeleteSpeaker,
  handleOpenAddSpeakerModal,
  handleShouldShowActionButtonsAfterSpeakerTranscribed
}) => {
  const userPermissions = useSelector(
    (state: { userDetailsPermission: IUserDetailsPermissionState }) =>
      state.userDetailsPermission.rolePermission
  );

  const [agendaTitle, setAgendaTitle] = useState<Record<number, any>>({});

  const [copyTransactions, setCopyTransactions] = useState<any>();

  const [currentTime, setCurrentTime] = useState<Date>(new Date());

  // Update current time every second to check if 60 seconds have passed for add speaker button
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000); // Update every second

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const copiedTranscriptions = [...transcriptions];

    const agendaMap: Record<string, any[]> = {};

    // Group agendas by title
    copiedTranscriptions.forEach((item) => {
      if (!agendaMap[item.agenda_id]) {
        agendaMap[item.agenda_id] = [];
      }
      agendaMap[item.agenda_id].push(item);
    });

    // Process each group
    Object.entries(agendaMap).forEach(([_, agendas]) => {
      const anyStopped = agendas.some((a) => a.status?.final === AGENDA.STOP);
      agendas.forEach((a) => {
        a.isActive = !anyStopped;
      });
    });

    setCopyTransactions(copiedTranscriptions);
  }, [transcriptions]);

  const currAgenda = copyTransactions?.find(
    (item: { id: any }) => item.id === agenda?.id
  );
  const isActiveAgenda = currAgenda?.isActive;

  return (
    <>
      <Box
        key={index}
        className={`agenda-container ${isAgenda(agenda) ? "agenda-item" : ""}`}
        sx={{
          backgroundColor: agenda?.id === navigateSpeechId ? "lightblue" : "",
        }}
      >
        <Box
          className={`start-end-container ${agenda.isAgendaEditing && (agenda.status.initial === AGENDA.START || agenda.status.initial === AGENDA.RESUME) ? 'editing-mode' : ''}`}
          style={{
            backgroundColor: getStatusColor(agenda?.status?.initial),
          }}
        >
          <>
            {agenda.isAgendaEditing ? (
              <>
                {agenda.status.initial === AGENDA.START ? (
                  <>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <TextField
                        className="text-box"
                        value={agendaTitle[index] ?? agenda.title ?? ""}
                        onChange={
                          (e) =>
                            setAgendaTitle({
                              ...agendaTitle,
                              [index]: e.target.value,
                            })
                          //updateAgendaTitle(e.target.value, index)
                        }
                        disabled={
                          !hasPermission(
                            userPermissions,
                            PERMISSION.MODULE?.MEETING_TRANSCRIPTION_PAGE,
                            PERMISSION?.ACTION?.START_AGENDA
                          )
                        }
                      />
                      {hasPermission(
                        userPermissions,
                        PERMISSION.MODULE?.MEETING_TRANSCRIPTION_PAGE,
                        PERMISSION?.ACTION?.START_AGENDA
                      ) && (
                          <>
                            <IconButton
                              sx={{ p: 0 }}
                              className="edit-icon"
                              onClick={() =>
                                handleTitleSaveClick(index, agendaTitle[index])
                              }
                              disabled={
                                (agendaTitle[index]?.length ?? 0) < 3 ||
                                !hasPermission(
                                  userPermissions,
                                  PERMISSION.MODULE?.MEETING_TRANSCRIPTION_PAGE,
                                  PERMISSION?.ACTION?.START_AGENDA
                                )
                              }
                            >
                              <CheckIcon />
                            </IconButton>
                          </>
                        )}
                    </Box>
                  </>
                ) : (
                  <>
                    {agenda?.status?.initial === AGENDA.RESUME ? (
                      <>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Box className="country-textfield">
                            <Autocomplete
                              value={selectedValue}
                              options={agendaOptions}
                              getOptionLabel={(option) => option?.label} // Specify how to display the options
                              isOptionEqualToValue={(option, value) =>
                                option.value === value.value
                              } // Match by `id`
                              onChange={(_event, newValue) => {
                                if (newValue) {
                                  setSelectedValue(newValue);
                                  /* handleAgendaTitleChange(
                                    newValue,
                                    agenda,
                                    index
                                  ); */
                                }
                              }} // Capture selected value
                              disabled={
                                !hasPermission(
                                  userPermissions,
                                  PERMISSION.MODULE?.MEETING_TRANSCRIPTION_PAGE,
                                  PERMISSION?.ACTION?.START_AGENDA
                                )
                              }
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  placeholder="Select Agenda Item Summary"
                                />
                              )}
                            />
                          </Box>
                          {/* {userRole === USER.USER_ROLE.ADMIN && ( */}

                          {hasPermission(
                            userPermissions,
                            PERMISSION.MODULE?.MEETING_TRANSCRIPTION_PAGE,
                            PERMISSION?.ACTION?.START_AGENDA
                          ) && (
                              <>
                                <IconButton
                                  sx={{ p: 0 }}
                                  className="edit-icon"
                                  onClick={() =>
                                    handleTitleSaveClick(index, selectedValue)
                                  }
                                  disabled={
                                    !selectedValue ||
                                    selectedValue?.label?.length < 3 ||
                                    !hasPermission(
                                      userPermissions,
                                      PERMISSION.MODULE
                                        ?.MEETING_TRANSCRIPTION_PAGE,
                                      PERMISSION?.ACTION?.START_AGENDA
                                    )
                                  }
                                >
                                  <CheckIcon />
                                </IconButton>
                              </>
                            )}
                        </Box>
                      </>
                    ) : (
                      <></>
                    )}
                  </>
                )}
              </>
            ) : (
              <>
                <AppTitleTypography
                  text={agenda?.title}
                  maxLength={AGENDA_DATA.AGENDA_NAME_MAX_LENGTH}
                  className="typography-with-text agenda-typography-ellipsis"
                />
                <Box>
                  {/* {userRole === USER.USER_ROLE.ADMIN && ( */}
                  {(((agenda?.status?.initial === AGENDA.RESUME &&
                    agenda?.status?.final === "") || (agenda?.status?.initial === AGENDA.START))
                    &&
                    isActiveAgenda) &&
                    hasPermission(
                      userPermissions,
                      PERMISSION.MODULE?.MEETING_TRANSCRIPTION_PAGE,
                      PERMISSION?.ACTION?.START_AGENDA
                    ) && (
                      <>
                        <IconButton
                          sx={{ p: 0 }}
                          className="edit-icon"
                          onClick={() => handleTitleEditClick(index)}
                        >
                          <EditIcon />
                        </IconButton>
                      </>
                    )}
                </Box>
              </>
            )}
          </>
        </Box>

        {agenda?.speakers?.length > 0 && (
          <>
            <Box className="agenda-speakers">
              {agenda?.speakers?.map(
                (speaker: Speaker, speakerIndex: number) => {
                  const speakerKey = speaker.speech_id || `speaker-${speaker.id || speakerIndex}`;
                  const canAddSection = hasPermission(
                    userPermissions,
                    PERMISSION?.MODULE?.MEETING_TRANSCRIPTION_PAGE,
                    PERMISSION?.ACTION?.MERGE_TRANSCRIPTION
                  );
                  return (
                    <React.Fragment key={speakerKey}>
                      <Box>
                        <StandaloneSpeaker
                          speaker={speaker}
                          index={speakerIndex}
                          agenda={agenda}
                          agendaIndex={index}
                          agendaStart={agendaStart}
                          navigateSpeechId={navigateSpeechId}
                          generateSummary={generateSummary}
                          currentAgenda={currentAgenda}
                          handlePauseStopAgenda={handlePauseStopAgenda}
                          handleAddAgendaStart={handleAddAgendaStart}
                          handleAddAgendaEnd={handleAddAgendaEnd}
                          agendaOptions={agendaOptions}
                          renderedSpeakers={renderedSpeakers}
                          isAgendaBroadcasting={isAgendaBroadcasting}
                          agendaStartIndex={agendaStartIndex}
                          successiveAgendaStartIndex={successiveAgendaStartIndex}
                          handleEditSpeaker={handleEditSpeaker}
                          handleDeleteSpeaker={handleDeleteSpeaker}
                          transcriptions={transcriptions}
                          actualIndex={speakerIndex}
                          currentTime={currentTime}
                          handleShouldShowActionButtonsAfterSpeakerTranscribed={handleShouldShowActionButtonsAfterSpeakerTranscribed}
                        />
                      </Box>
                      {canAddSection && handleOpenAddSpeakerModal && (
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            py: 1,
                            position: "relative",
                          }}
                        >
                          <Typography
                            component="span"
                            onClick={() => handleOpenAddSpeakerModal(index, speakerIndex, speaker)}
                            sx={{
                              color: "primary.main",
                              cursor: "pointer",
                              textDecoration: "underline",
                              fontSize: "14px",
                              fontWeight: 500,
                              "&:hover": {
                                color: "primary.dark",
                                textDecoration: "underline",
                              },
                            }}
                          >
                            Add new speaker section
                          </Typography>
                        </Box>
                      )}
                    </React.Fragment>
                  );
                }
              )}
            </Box>

            <Box
              key={agenda.id}
              ref={(el) => {
                if (el) {
                  agendaRefs.current[agenda.id] = el;
                }
              }}
              className="start-end-container"
              style={{
                backgroundColor: getStatusColor(agenda?.status?.final),
              }}
            >
              <AppTitleTypography
                text={
                  agenda?.status?.final === AGENDA.STOP
                    ? `${agenda?.title || ""} (summary)`
                    : `${agenda?.title || ""} - paused`
                }
                maxLength={AGENDA_DATA.AGENDA_NAME_MAX_LENGTH}
                className="typography-with-text agenda-typography-ellipsis"
              />
            </Box>
          </>
        )}
      </Box>
    </>
  );
};

export default Agenda;
